import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useStore, buildStorePath } from './StoreContext';
import { getPresserSession, pressingFetch, clearPressingSession, authenticatedFetch, useAuth } from './AuthComponent';
import { useBarcodeScanner } from './hooks';
import { playSuccess, playError, preloadSounds } from './sounds';

import { hasRole } from './constants/roles';
// Mirrors bulk_shipping.classify_scan on the server: UPS tracking = box label.
const UPS_1Z = /^1Z[0-9A-Z]{16}$/;
const UPS_NUM = /^(\d{12}|\d{18})$/;
// The routing barcode under the big "MN 554 9-84" text is 420 + the ship-to
// ZIP+4 — 12 digits, so it used to pass as a UPS reference and open a box that
// never tracked. Caught before UPS_NUM.
const UPS_ROUTING = /^420\d{9}$/;
export const ROUTING_SCAN_MESSAGE =
    'That is the routing barcode (420 + the ZIP code). Scan the 1Z tracking barcode at the bottom of the label.';
export const INVALID_LABEL_MESSAGE =
    'That is not a valid UPS tracking number. Scan the 1Z barcode at the bottom of the label.';
// UPS check digit over the 15 characters between "1Z" and the final digit.
// Mirrors bulk_shipping.ups_check_digit — without it any "1Z" + 16 characters
// opened a box that could never track.
export function upsCheckDigit(body15) {
    let total = 0;
    for (let i = 0; i < body15.length; i++) {
        const ch = body15[i];
        const v = ch >= '0' && ch <= '9' ? Number(ch) : (ch.charCodeAt(0) - 63) % 10;
        total += i % 2 === 0 ? v : v * 2;
    }
    return (10 - (total % 10)) % 10;
}
export function isValidUps1Z(code) {
    const norm = (code || '').replace(/\s+/g, '').toUpperCase();
    if (!UPS_1Z.test(norm)) return false;
    return String(upsCheckDigit(norm.slice(2, 17))) === norm[17];
}
export function classifyScan(code) {
    const norm = (code || '').replace(/\s+/g, '').toUpperCase();
    if (UPS_ROUTING.test(norm)) return { kind: 'routing', value: norm };
    if (UPS_1Z.test(norm) || UPS_NUM.test(norm)) return { kind: 'label', value: norm };
    return { kind: 'order', value: (code || '').trim() };
}
const REASON_TEXT = {
    cancelled:       'Order is cancelled',
    not_transferred: 'Not transferred to a store yet — finish it in Packing first',
    received:        'Store already received this fulfillment',
    other_store:     'This order goes to a different store',
    already_labeled: 'Already in this box',
    already_shipped: 'Already shipped in an earlier box — only the new fulfillment goes in this one',
    order_not_found: 'Order not found at this location',
    shopify_unavailable: 'Shopify is not responding — try again in a moment',
    box_shipped:     'This box was shipped; rescan the label to reopen it',
    box_other_store: 'This label is already a box to another store',
};
const otherStoreText = d => `${d.tracking_number || 'This label'} is a box to ${d.box_store || 'another store'} with ${d.orders} order${d.orders === 1 ? '' : 's'} — switch to that store, or use a new label`;
const upsUrl = t => `https://www.ups.com/track?loc=en_US&tracknum=${encodeURIComponent(t)}`;
const fmtWhen = iso => iso ? new Date(iso).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '';

// consoleMode (2026-09-03): sidebar entry for signed-in admin / site_manager /
// user accounts. No PIN — the location list comes from /api/locations (JWT
// scoped: admins see every active Soccer Post location, narrowed by emulation;
// everyone else only their assigned sites) and the API calls carry
// presser_id=0 so the server attributes scans to the signed-in username.
export default function BulkShippingPortal({ consoleMode = false }) {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { user, emulating } = useAuth();
    // Console mode + sidebar on "All Stores": the portal ships from ONE
    // store's warehouses, so ask for the store first (Eric 2026-09-10).
    const { activeStore, accessibleStores, STORE_DISPLAY_NAMES: storeNames } = useStore();
    const routerLocation = useLocation();
    const [session] = useState(() => (consoleMode ? null : getPresserSession()));
    const api = consoleMode ? authenticatedFetch : pressingFetch;
    const presserId = consoleMode ? 0 : session?.presser_id;
    const [consoleLocs, setConsoleLocs] = useState(null);
    useEffect(() => {
        if (!consoleMode) return;
        let cancelled = false;
        authenticatedFetch('/api/locations?store=soccerpost')
            .then(r => (r.ok ? r.json() : []))
            .then(rows => {
                if (cancelled) return;
                let active = (rows || []).filter(l => l.active !== false && l.shopify_location_id && String(l.shopify_location_id) !== '0');
                if (hasRole(user, ['admin']) && emulating?.shopify_location_id) {
                    active = active.filter(l => String(l.shopify_location_id) === String(emulating.shopify_location_id));
                }
                setConsoleLocs(active.map(l => ({ shopify_location_id: String(l.shopify_location_id), location_name: l.name, store_key: 'soccerpost' })));
            })
            .catch(() => { if (!cancelled) setConsoleLocs([]); });
        return () => { cancelled = true; };
    }, [consoleMode, user, emulating]);
    const assignments = useMemo(
        () => (consoleMode ? (consoleLocs || []) : (session?.assignments || [])),
        [consoleMode, consoleLocs, session]);
    // Source location (user rules 2026-09-03):
    //  - Only Soccer Post assignments count here (other brands' copies of the
    //    same site, e.g. Surf "Teams Lancaster", are not offered).
    //  - A PIN with exactly ONE Soccer Post location goes straight in with
    //    that location as the source, whatever it is called.
    //  - A PIN with several Soccer Post locations is asked to pick the one
    //    they are at, from the "Teams …" fulfillment locations (or from all
    //    of their Soccer Post locations if none of them is a Teams site).
    const spLocs = useMemo(() => {
        const seen = new Set();
        return assignments
            .filter(a => a.store_key === 'soccerpost')
            .filter(a => { const k = String(a.shopify_location_id); if (seen.has(k)) return false; seen.add(k); return true; })
            .sort((x, y) => (x.location_name || '').localeCompare(y.location_name || ''));
    }, [assignments]);
    const pickerLocs = useMemo(() => {
        if (spLocs.length <= 1) return spLocs;
        const teams = spLocs.filter(a => /^Teams\s/i.test(a.location_name || a.name || ''));
        return teams.length ? teams : spLocs;
    }, [spLocs]);
    const [fromLoc, setFromLoc] = useState(null);
    // ?loc=<shopify_location_id> preselects the from-location when it is one
    // of the caller's locations (direct links from the sidebar / bookmarks);
    // otherwise a single location auto-selects and several ask.
    useEffect(() => {
        if (fromLoc) return;
        const hint = searchParams.get('loc');
        const hinted = hint && spLocs.find(a => String(a.shopify_location_id) === String(hint));
        if (hinted) { setFromLoc(hinted); return; }
        if (spLocs.length === 1) setFromLoc(spLocs[0]);
    }, [spLocs, fromLoc, searchParams]);
    const [stores, setStores] = useState([]);
    const [showAll, setShowAll] = useState(false);
    const [storeQuery, setStoreQuery] = useState('');
    // Alphabetical, filtered by the search box (name match, case-insensitive).
    const visibleStores = useMemo(() => {
        const q = storeQuery.trim().toLowerCase();
        return [...stores]
            .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
            .filter(s => !q || (s.name || '').toLowerCase().includes(q));
    }, [stores, storeQuery]);
    const [store, setStore] = useState(null);
    const [label, setLabel] = useState(null);           // current box tracking number
    const [boxMeta, setBoxMeta] = useState(null);        // {id, status, shipped_at, reopened_at, ...}
    const [box, setBox] = useState([]);                 // [{order_id, order_name, rows:[...]}]
    const [pending, setPending] = useState(null);       // order scanned before any label
    const [notice, setNotice] = useState(null);         // {tone:'ok'|'warn'|'stop', text}
    const [busy, setBusy] = useState(false);
    const [manual, setManual] = useState('');
    const [handForm, setHandForm] = useState(false);    // "No UPS label — driving it over" inline form open?
    const [handName, setHandName] = useState('');
    const noticeTimer = useRef(null);

    useEffect(() => {
        if (consoleMode) { if (!user) navigate('/login', { replace: true }); return; }
        if (!session) navigate('/pressing-portal/login', { replace: true });
    }, [consoleMode, user, session, navigate]);

    // Same audio cues as the Packing Portal: success chime on a good scan,
    // error buzz on a block / mismatch / "already in this box" (2026-09-03).
    useEffect(() => { preloadSounds(); }, []);
    const say = useCallback((tone, text) => {
        if (tone === 'ok') playSuccess(); else playError();
        setNotice({ tone, text });
        clearTimeout(noticeTimer.current);
        noticeTimer.current = setTimeout(() => setNotice(null), tone === 'ok' ? 4000 : 8000);
    }, []);

    const q = (path, params) => {
        const usp = new URLSearchParams({ presser_id: presserId, ...params });
        return api(`${path}?${usp}`).then(r => r.json());
    };

    // Store picker data
    useEffect(() => {
        if (!fromLoc) return;
        q('/api/bulk-shipping/stores', { from_location_id: fromLoc.shopify_location_id, all: showAll ? 1 : 0 })
            .then(d => setStores(d.stores || []))
            .catch(() => setStores([]));
    }, [fromLoc, showAll]);            // eslint-disable-line react-hooks/exhaustive-deps

    // Resume a box when a store is chosen
    useEffect(() => {
        if (!fromLoc || !store) return;
        setHandForm(false); setHandName('');
        q('/api/bulk-shipping/box', { from_location_id: fromLoc.shopify_location_id, to_location_id: store.shopify_location_id })
            .then(d => { setLabel(d.tracking_number || null); setBox(d.orders || []); setBoxMeta(d.box || null); })
            .catch(() => { setLabel(null); setBox([]); setBoxMeta(null); });
    }, [fromLoc, store]);              // eslint-disable-line react-hooks/exhaustive-deps

    const attach = useCallback(async (orderQuery, tracking) => {
        setBusy(true);
        try {
            const res = await api('/api/bulk-shipping/attach', {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ presser_id: presserId,
                    from_location_id: fromLoc.shopify_location_id, to_location_id: store.shopify_location_id,
                    tracking_number: tracking, order_query: orderQuery }),
            });
            const data = await res.json();
            if (res.status === 404) { say('stop', `${orderQuery}: ${REASON_TEXT.order_not_found}`); return; }
            if (res.status === 409) {
                const d = data.detail || data;
                if (d.error === 'box_other_store') { say('stop', otherStoreText(d)); return; }
                if (d.error === 'already_shipped') {
                    const prev = (d.skipped || []).map(x => x.tracking_number).filter(Boolean)[0];
                    say('stop', `${d.order_name || orderQuery}: already shipped${prev ? ` in box ${prev}` : ' in an earlier box'} — nothing new to add`); return;
                }
                say('stop', `${d.order_name || orderQuery}: ${REASON_TEXT[d.error] || d.error}`); return;
            }
            if (res.status === 502) {
                const d = data.detail || {};
                say('stop', `${d.order_name || orderQuery}: ${REASON_TEXT.shopify_unavailable}`); return;
            }
            if (!res.ok) {
                const d = data.detail;
                const msg = typeof d === 'string' ? d : (d && (d.error || d.message)) || res.status;
                say('stop', `Could not attach: ${msg}`); return;
            }
            if (data.already) { say('warn', `${data.order.order_name}: ${REASON_TEXT.already_labeled}`); return; }
            const skippedTxt = (data.skipped || []).length
                ? ` (${data.skipped.length} fulfillment skipped: ${[...new Set(data.skipped.map(s => REASON_TEXT[s.reason] || s.reason))].join('; ')})` : '';
            say(data.shopify_mirror === 'failed' ? 'warn' : 'ok',
                `${data.order.order_name} added to box${skippedTxt}${data.shopify_mirror === 'failed' ? ' — Shopify not updated yet, will retry on next scan' : ''}`);
            setBoxMeta(m => m ? { ...m, status: m.status === 'shipped' ? 'reopened' : m.status } : m);
            setBox(prev => {
                const rows = data.attached.map(a => ({ id: a.row_id, fulfillment_order_id: a.fo_id, tracking_number: tracking, scanned_at: new Date().toISOString() }));
                const i = prev.findIndex(o => o.order_id === data.order.order_id);
                if (i >= 0) { const cp = [...prev]; cp[i] = { ...cp[i], rows: [...cp[i].rows, ...rows] }; return cp; }
                return [...prev, { order_id: data.order.order_id, order_name: data.order.order_name, rows }];
            });
        } catch (e) {
            say('stop', `Network error: ${e.message}`);
        } finally { setBusy(false); }
    }, [api, presserId, fromLoc, store, say]);

    // "No UPS label — driving it over" override (2026-09-10): creates a box
    // via a typed name instead of a scanned UPS number. Mirrors the label
    // branch of handleScan below (shipped → reopen; carry over any pending
    // order) but never touches classifyScan — the API decides everything.
    const createHandBox = useCallback(async () => {
        const name = handName.trim();
        if (!name || busy || !fromLoc || !store) return;
        setBusy(true);
        try {
            const res = await api('/api/bulk-shipping/label', {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ presser_id: presserId, from_location_id: fromLoc.shopify_location_id,
                                       to_location_id: store.shopify_location_id, hand_carried: true, label_name: name }),
            });
            const data = await res.json();
            if (!res.ok) {
                const d = data.detail;
                const msg = typeof d === 'string' ? d : (d && (d.error || d.message)) || res.status;
                say('stop', msg);
                return;
            }
            let meta = data.box;
            const tn = meta.tracking_number;
            if (meta.status === 'shipped') {
                // Same rule as a rescanned UPS label: reopen for more orders.
                const r2 = await api('/api/bulk-shipping/reopen', {
                    method: 'POST', headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ presser_id: presserId, from_location_id: fromLoc.shopify_location_id, tracking_number: tn }),
                });
                const d2 = await r2.json();
                if (r2.ok) { meta = d2.box; say('warn', `Box "${tn}" was shipped ${fmtWhen(data.box.shipped_at)} — reopened for more orders`); }
                else { say('stop', `Could not reopen box "${tn}": ${(d2.detail && (d2.detail.error || d2.detail)) || r2.status}`); }
            } else {
                say('ok', `Box "${tn}" created — scan orders`);
            }
            setLabel(tn);
            setBoxMeta(meta);
            setBox(data.orders || []);
            setHandForm(false);
            setHandName('');
            if (pending) { attach(pending, tn); setPending(null); }
        } catch (e) {
            say('stop', `Network error: ${e.message}`);
        } finally { setBusy(false); }
    }, [api, presserId, fromLoc, store, handName, busy, say, pending, attach]);

    const handleScan = useCallback((code) => {
        if (!store || busy) return;
        const c = classifyScan(code);
        if (c.kind === 'routing') { say('stop', ROUTING_SCAN_MESSAGE); return; }
        if (c.kind === 'label') {
            const changed = c.value !== label;
            if (changed) { setLabel(c.value); setBox([]); setBoxMeta(null); }
            (async () => {
                setBusy(true);
                try {
                    const res = await api('/api/bulk-shipping/label', {
                        method: 'POST', headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ presser_id: presserId, from_location_id: fromLoc.shopify_location_id,
                                               to_location_id: store.shopify_location_id, tracking_number: c.value }),
                    });
                    const data = await res.json();
                    if (res.status === 409 && data.detail?.error === 'box_other_store') { setLabel(null); say('stop', otherStoreText(data.detail)); return; }
                    // A 422 carries a plain-language reason (routing barcode, bad
                    // check digit) — show it as-is rather than wrapping it.
                    if (res.status === 422 && typeof data.detail === 'string') { setLabel(null); say('stop', data.detail); return; }
                    if (!res.ok) { say('stop', `Could not read box: ${(data.detail && (data.detail.error || data.detail)) || res.status}`); return; }
                    let meta = data.box;
                    if (meta.status === 'shipped') {
                        // Spec: rescanning a shipped label reopens it for more orders.
                        const r2 = await api('/api/bulk-shipping/reopen', {
                            method: 'POST', headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ presser_id: presserId, from_location_id: fromLoc.shopify_location_id, tracking_number: c.value }),
                        });
                        const d2 = await r2.json();
                        if (r2.ok) { meta = d2.box; say('warn', `Box ${c.value} was shipped ${fmtWhen(data.box.shipped_at)} — reopened for more orders`); }
                        else { say('stop', `Could not reopen box ${c.value}: ${(d2.detail && (d2.detail.error || d2.detail)) || r2.status}`); meta = data.box; }
                    } else {
                        say('ok', changed ? `Box label set: ${c.value}` : 'Same box label');
                    }
                    setBoxMeta(meta);
                    setBox(data.orders || []);
                    if (pending) { attach(pending, c.value); setPending(null); }
                } catch (e) { say('stop', `Network error: ${e.message}`); }
                finally { setBusy(false); }
            })();
            return;
        }
        if (!label) { setPending(c.value); say('warn', `${c.value} is waiting — scan the box label to attach it`); return; }
        attach(c.value, label);
    }, [store, busy, label, pending, attach, say, api, presserId, fromLoc]);
    useBarcodeScanner(handleScan);

    const remove = async (row) => {
        if (!window.confirm(`Remove ${row.order_name || ''} fulfillment ${row.fulfillment_order_id} from box ${row.tracking_number}?`)) return;
        const res = await api('/api/bulk-shipping/remove', {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ presser_id: presserId, row_id: row.id }),
        });
        if (!res.ok) { say('stop', 'Could not remove'); return; }
        setBox(prev => prev.map(o => ({ ...o, rows: o.rows.filter(r => r.id !== row.id) })).filter(o => o.rows.length));
        say('ok', 'Removed from box');
    };

    const shipBox = async () => {
        if (!label) return;
        setBusy(true);
        try {
            const res = await api('/api/bulk-shipping/ship', {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ presser_id: presserId, from_location_id: fromLoc.shopify_location_id, tracking_number: label }),
            });
            const data = await res.json();
            if (!res.ok) { say('stop', `Could not ship box: ${(data.detail && (data.detail.error || data.detail)) || res.status}`); return; }
            say('ok', `Box shipped · ${count} order${count === 1 ? '' : 's'} · ${fmtWhen(data.box.shipped_at)}`);
            setLabel(null); setBox([]); setBoxMeta(null);
        } catch (e) { say('stop', `Network error: ${e.message}`); }
        finally { setBusy(false); }
    };

    const signOut = () => { clearPressingSession(); navigate('/pressing-portal/login', { replace: true }); };

    if (consoleMode ? !user : !session) return null;
    if (consoleMode && activeStore === 'all') {
        const choices = (accessibleStores || []).filter(k => k !== 'all');
        return (
            <div className="bsp bsp--console">
                <div className="bsp__head"><span>🚚 Bulk Shipping</span></div>
                <div className="bsp__pick">
                    <h2>Which store are you shipping for?</h2>
                    <p style={{ color: '#5c6b7a', marginTop: 0 }}>The Bulk Shipping Portal works on one store at a time. Pick a store to continue.</p>
                    {choices.map(k => (
                        <button key={k} className="bsp__store"
                                onClick={() => navigate(buildStorePath(k, routerLocation.pathname) + (routerLocation.search || ''))}>
                            {storeNames?.[k] || k}
                        </button>
                    ))}
                </div>
                <style>{`
                  .bsp{min-height:0;background:#f4f5f7;font-family:-apple-system,system-ui,sans-serif;color:#1f2a37;border-radius:12px;overflow:hidden}
                  .bsp__head{background:#1f2a37;color:#fff;padding:14px 20px;font-size:20px;font-weight:600}
                  .bsp__pick{max-width:720px;margin:24px auto;padding:0 16px}
                  .bsp__pick h2{font-size:22px;margin:0 0 6px}
                  .bsp__store{display:block;width:100%;text-align:left;background:#fff;border:1px solid #d9dee3;border-radius:12px;padding:16px 18px;font-size:18px;margin:10px 0;cursor:pointer}
                  .bsp__store:hover{border-color:#1f2a37}
                `}</style>
            </div>
        );
    }
    const count = box.reduce((n, o) => n + o.rows.length, 0);
    const actorName = consoleMode ? (user.username || user.sub || '') : `${session.first_name} ${session.last_name}`;

    return (
        <div className={`bsp${consoleMode ? ' bsp--console' : ''}`}>
            <style>{`
              .bsp{min-height:100vh;background:#f4f5f7;font-family:-apple-system,system-ui,sans-serif;color:#1f2a37}
              .bsp--console{min-height:0;border-radius:12px;overflow:hidden}
              .bsp__hdr{display:flex;align-items:center;gap:16px;padding:12px 20px;background:#1f2a37;color:#fff}
              .bsp__hdr h1{font-size:20px;margin:0;flex:1}
              .bsp__btn{border:0;border-radius:8px;padding:10px 16px;font-size:15px;font-weight:600;cursor:pointer;background:#e5e7eb;color:#1f2a37}
              .bsp__btn--dark{background:#374151;color:#fff}
              .bsp__body{padding:20px;max-width:960px;margin:0 auto}
              .bsp__grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(220px,1fr));gap:12px}
              .bsp__store{border:0;border-radius:12px;background:#fff;padding:18px;text-align:left;cursor:pointer;box-shadow:0 1px 2px rgba(0,0,0,.08);font-size:16px;font-weight:600}
              .bsp__store small{display:block;color:#6b7280;font-weight:400;margin-top:4px}
              .bsp__label{background:#fff;border-radius:12px;padding:16px 20px;display:flex;align-items:center;gap:18px;margin-bottom:14px;box-shadow:0 1px 2px rgba(0,0,0,.08)}
              .bsp__label b{font-family:ui-monospace,Menlo,monospace;font-size:22px;letter-spacing:.04em}
              .bsp__count{margin-left:auto;background:#1f2a37;color:#fff;border-radius:999px;padding:6px 14px;font-weight:700}
              .bsp__ship{background:#16a34a;color:#fff;border:0;border-radius:8px;padding:10px 18px;font-weight:700;cursor:pointer}.bsp__ship:disabled{opacity:.4;cursor:default}.bsp__pill{border-radius:999px;padding:4px 10px;font-size:13px;font-weight:700}.bsp__pill--amber{background:#fef3c7;color:#92400e}.bsp__pill--blue{background:#dbeafe;color:#1e40af}.bsp__pill--purple{background:#ede9fe;color:#5b21b6}
              .bsp__hand-toggle{margin-bottom:14px}
              .bsp__hand-form{display:flex;align-items:center;gap:10px;flex-wrap:wrap;margin-bottom:14px;background:#fff;border-radius:12px;padding:14px 16px;box-shadow:0 1px 2px rgba(0,0,0,.08)}
              .bsp__hand-form input{flex:1;min-width:200px;font-size:16px;padding:10px 12px;border:1px solid #d1d5db;border-radius:8px}
              .bsp__hand-counter{color:#6b7280;font-size:13px;white-space:nowrap}
              .bsp__notice{border-radius:10px;padding:12px 16px;margin-bottom:14px;font-weight:600}
              .bsp__notice--ok{background:#dcfce7;color:#166534}.bsp__notice--warn{background:#fef3c7;color:#92400e}.bsp__notice--stop{background:#fee2e2;color:#991b1b}
              .bsp__pending{background:#fef3c7;color:#92400e;border-radius:10px;padding:10px 16px;margin-bottom:14px}
              .bsp__list{background:#fff;border-radius:12px;box-shadow:0 1px 2px rgba(0,0,0,.08);overflow:hidden}
              .bsp__row{display:flex;align-items:center;gap:14px;padding:12px 16px;border-top:1px solid #eef0f3}
              .bsp__row:first-child{border-top:0}
              .bsp__row b{min-width:130px}.bsp__row span{color:#6b7280;font-size:14px}
              .bsp__rm{margin-left:auto;background:#fee2e2;color:#991b1b;border:0;border-radius:8px;padding:6px 12px;font-weight:600;cursor:pointer}
              .bsp__manual{display:flex;gap:8px;margin:14px 0}
              .bsp__manual input{flex:1;font-size:16px;padding:10px 12px;border:1px solid #d1d5db;border-radius:8px}
              .bsp__toggle{display:flex;align-items:center;gap:8px;margin:0 0 14px;color:#374151}
              .bsp__dd{max-width:560px;margin:0 auto;background:#fff;border-radius:12px;box-shadow:0 1px 2px rgba(0,0,0,.08);overflow:hidden}
              .bsp__dd-head{display:flex;flex-direction:column;gap:10px;padding:14px 16px;border-bottom:1px solid #eef0f3}
              .bsp__dd-search{font-size:17px;padding:12px 14px;border:1px solid #d1d5db;border-radius:8px;width:100%;box-sizing:border-box}
              .bsp__dd-list{max-height:60vh;overflow-y:auto}
              .bsp__dd-item{display:flex;justify-content:space-between;align-items:center;width:100%;border-radius:0;box-shadow:none;border-top:1px solid #eef0f3;padding:14px 16px}
              .bsp__dd-item:first-child{border-top:0}
              .bsp__dd-item:hover{background:#f3f4f6}
              .bsp__dd-count{color:#6b7280;font-weight:500;font-size:14px;white-space:nowrap}
              .bsp__dd-empty{padding:16px;color:#6b7280}
            `}</style>
            <div className="bsp__hdr">
                <h1>🚚 Bulk Shipping{fromLoc ? ` · ${fromLoc.location_name || fromLoc.name}` : ''}{store ? ` → ${store.name}` : ''}</h1>
                <span>{actorName}</span>
                {store && <button className="bsp__btn" onClick={() => { setStore(null); setLabel(null); setBox([]); setBoxMeta(null); setPending(null); setHandForm(false); setHandName(''); }}>Close store</button>}
                {fromLoc && !store && spLocs.length > 1 && <button className="bsp__btn" onClick={() => setFromLoc(null)}>Change location</button>}
                {!consoleMode && <button className="bsp__btn bsp__btn--dark" onClick={signOut}>Sign out</button>}
            </div>
            <div className="bsp__body">
                {!fromLoc && (
                    <div className="bsp__grid">
                        {pickerLocs.length > 1 && <p style={{ gridColumn: '1 / -1', margin: 0, color: '#374151' }}>Which location are you shipping from?</p>}
                        {pickerLocs.length === 0 && (consoleMode
                            ? <p>{consoleLocs === null ? 'Loading your locations…' : 'Your account has no Soccer Post location. Ask an admin to assign one in Settings → Users.'}</p>
                            : <p>This PIN has no Soccer Post location assigned. Ask an admin to add one in Settings → Portal Users.</p>)}
                        {pickerLocs.map(a => (
                            <button key={`${a.store_key}:${a.shopify_location_id}`} className="bsp__store" onClick={() => setFromLoc(a)}>{a.location_name || a.name}</button>
                        ))}
                    </div>
                )}
                {fromLoc && !store && (
                    <div className="bsp__dd">
                        <div className="bsp__dd-head">
                            <input className="bsp__dd-search" value={storeQuery} onChange={e => setStoreQuery(e.target.value)}
                                   placeholder="Search stores…" autoFocus />
                            <label className="bsp__toggle" style={{ margin: 0 }}><input type="checkbox" checked={showAll} onChange={e => setShowAll(e.target.checked)} /> Show all stores</label>
                        </div>
                        <div className="bsp__dd-list" role="listbox">
                            {visibleStores.length === 0 && <div className="bsp__dd-empty">{stores.length === 0 ? 'No orders are waiting to ship from here.' : 'No store matches that search.'}</div>}
                            {visibleStores.map(s => (
                                <button key={s.shopify_location_id} className="bsp__store bsp__dd-item" role="option" onClick={() => { setStore(s); setStoreQuery(''); }}>
                                    <span>{s.name}</span>
                                    <span className="bsp__dd-count">{s.waiting} order{s.waiting === 1 ? '' : 's'}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                )}
                {store && (
                    <>
                        <div className="bsp__label">
                            <span>Box label</span>
                            <b>{label || 'Scan a box label'}</b>
                            {boxMeta?.service_code === 'HAND' && <span className="bsp__pill bsp__pill--purple">Hand-carried</span>}
                            {boxMeta?.status === 'reopened' && <span className="bsp__pill bsp__pill--amber">Reopened</span>}
                            {boxMeta?.status === 'shipped' && <span className="bsp__pill bsp__pill--blue">Shipped {fmtWhen(boxMeta.shipped_at)}</span>}
                            <span className="bsp__count">{count} in box</span>
                            <button className="bsp__ship" disabled={!label || count === 0 || busy || boxMeta?.status === 'shipped'} onClick={shipBox}>Ship box</button>
                        </div>
                        {!handForm && (
                            <div className="bsp__hand-toggle">
                                <button className="bsp__btn" onClick={() => setHandForm(true)}>No UPS label — driving it over</button>
                            </div>
                        )}
                        {handForm && (
                            <form className="bsp__hand-form" onSubmit={e => { e.preventDefault(); createHandBox(); }}>
                                <input
                                    value={handName}
                                    onChange={e => setHandName(e.target.value.slice(0, 30))}
                                    maxLength={30}
                                    placeholder="e.g. Randall Driving"
                                    autoFocus
                                />
                                <span className="bsp__hand-counter">{handName.length}/30</span>
                                <button className="bsp__btn bsp__btn--dark" type="submit" disabled={busy || !handName.trim()}>Create box</button>
                                <button className="bsp__btn" type="button" onClick={() => { setHandForm(false); setHandName(''); }}>Cancel</button>
                            </form>
                        )}
                        {notice && <div className={`bsp__notice bsp__notice--${notice.tone}`}>{notice.text}</div>}
                        {pending && <div className="bsp__pending">Waiting for a box label: <b>{pending}</b></div>}
                        <form className="bsp__manual" onSubmit={e => { e.preventDefault(); if (manual.trim()) { handleScan(manual.trim()); setManual(''); } }}>
                            <input value={manual} onChange={e => setManual(e.target.value)} placeholder="Type an order number or tracking number if the scanner fails" />
                            <button className="bsp__btn" type="submit" disabled={busy}>Enter</button>
                        </form>
                        <div className="bsp__list">
                            {box.length === 0 && <div className="bsp__row"><span>No orders in this box yet.</span></div>}
                            {box.flatMap(o => o.rows.map(r => (
                                <div className="bsp__row" key={r.id}>
                                    <b>{o.order_name}</b>
                                    <span>FO {r.fulfillment_order_id}</span>
                                    <span>{boxMeta?.service_code === 'HAND' ? r.tracking_number : <a href={upsUrl(r.tracking_number)} target="_blank" rel="noreferrer">{r.tracking_number}</a>}</span>
                                    <span>{new Date(r.scanned_at).toLocaleString()}</span>
                                    <button className="bsp__rm" onClick={() => remove({ ...r, order_name: o.order_name })}>Remove</button>
                                </div>
                            )))}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
